import {
    Component,
    computed,
    effect,
    EventEmitter,
    inject,
    Input,
    Output,
    signal
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { AppAvailableDatesSelectorComponent } from "@app/shared/components/app-dates-interval-selector/app-available-dates-selector.component";
import { FormDreBalanceOptionsComponent } from "@app/shared/components/form-dre-balance-options/form-dre-balance-options.component";
import { SharedMaterialModule } from "@app/shared/modules/shared-material.module";
import { PeriodNames } from "@arch/_shared/types/interval-dates";
import {
    DREVerticalReferences,
    VisualizationOptionsType
} from "@arch/_shared/types/options-visualization";
import { CompanyEntity, ReportEntity } from "@arch/domain/entity";
import { ScenarioEntity } from "@arch/domain/entity/scenario.entity";
import { GetAvailableController } from "@arch/presentation/controllers/get-available.controller";
import { EventChangeComparationParams } from "../report-toolbar/report-toolbar.component";
@Component({
    selector: "app-report-adjustments-comparation",
    imports: [
        SharedMaterialModule,
        FormsModule,
        AppAvailableDatesSelectorComponent,
        FormDreBalanceOptionsComponent
    ],
    templateUrl: "./report-adjustments-comparation.component.html",
    styleUrl: "./report-adjustments-comparation.component.scss"
})
export class ReportAdjustmentsComparationComponent {
    @Input({ required: true }) report: ReportEntity;
    @Input({ required: true }) company: CompanyEntity;
    @Input({ required: true }) scenarios: ScenarioEntity[];
    @Input({ required: false }) dropdownConfigs: {
        equalYear: boolean;
    };
    @Input({ required: true }) set options(value: VisualizationOptionsType) {
        this.optionsSignal.set(value);
    }
    get options() {
        return this.optionsSignal();
    }
    private optionsSignal = signal<VisualizationOptionsType>(null);
    @Output() handleChangeComparation =
        new EventEmitter<EventChangeComparationParams>();
    @Output() handleResetPeriod = new EventEmitter<void>();

    private readonly getAvailableController = inject(GetAvailableController);

    public formDreBalanceOptions: {
        analysisHorizontal: boolean;
        analysisVertical: boolean;
        verticalReference: DREVerticalReferences;
    } = {
        analysisHorizontal: false,
        analysisVertical: false,
        verticalReference: "RECEITA BRUTA"
    };

    constructor() {
        effect(() => {
            if (!this.options) return null;
            this.formDreBalanceOptions.analysisHorizontal =
                this.options.dre?.analysisHorizontal ||
                this.options.balance?.analysisHorizontal;
            this.formDreBalanceOptions.analysisVertical =
                (this.options.dre?.analysisVertical ||
                    this.options.balance?.analysisVertical) &&
                this.options.comparation.active;
            this.formDreBalanceOptions.verticalReference =
                this.options.dre?.verticalReference;
        });
    }

    public readonly comparationAvailable = computed((): Date[] => {
        if (!this.options) return null;
        const available = this.getAvailableController.execute({
            companyId: this.company.props._id,
            source: this.options.source,
            sources: this.options.sources,
            scenarioId: this.options.comparation.scenario.props._id
        });
        return available;
    });

    public onApplyComparation(period: {
        scenario: ScenarioEntity;
        periodName: PeriodNames;
        startDate: Date;
        endDate: Date;
        allMonthsCompare?: boolean;
    }) {
        const eventParams: EventChangeComparationParams = {
            scenario: period.scenario,
            periodName: period.periodName,
            dateBase: period.startDate,
            comparationDate: period.endDate,
            allMonthsCompare: period.allMonthsCompare
        };
        if (["dre", "dreComparation"].includes(this.report.type)) {
            eventParams.dre = {
                analysisHorizontal:
                    this.formDreBalanceOptions.analysisHorizontal,
                analysisVertical: this.formDreBalanceOptions.analysisVertical,
                verticalReference: this.formDreBalanceOptions.verticalReference
            };
        }
        if (this.report.hasBalance) {
            eventParams.balance = {
                analysisHorizontal:
                    this.formDreBalanceOptions.analysisHorizontal,
                analysisVertical: this.formDreBalanceOptions.analysisVertical
            };
        }
        this.handleChangeComparation.emit(eventParams);
    }

    public onSelectScenarioComparation(event: ScenarioEntity): void {
        this.optionsSignal.update((prev) => ({
            ...prev,
            comparation: {
                ...prev.comparation,
                scenario: event
            }
        }));
    }
}
